import React, { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { IOffer } from '@rbi-ctg/menu';
import { PromotionType } from 'generated/graphql-gateway';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { LoyaltyOffer } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';

import Offer from './offer';

interface ICartOfferProps {
  selectedOffer: IOffer | LoyaltyOffer;
  onRemove: () => void;
  textOfferRequirementsNotMet: React.ReactNode;
  isValid: boolean;
}

export const CartOffer: FC<ICartOfferProps> = ({
  selectedOffer,
  onRemove,
  textOfferRequirementsNotMet,
  isValid,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Offer
      onRemove={onRemove}
      textRemove={formatMessage({ id: 'remove' })}
      textOfferRequirementsNotMet={textOfferRequirementsNotMet}
      bgColor="#eae7e5"
      fill={Styles.color.grey.five}
      key={selectedOffer?._id}
      borderColor="#b6b6b6"
      selectedOffer={selectedOffer}
      isValid={isValid}
    />
  );
};

export const LoyaltyDiscountCartOffer: FC<{ selectedLoyaltyOffer: LoyaltyOffer }> = ({
  selectedLoyaltyOffer,
}) => {
  const dispatch = useAppDispatch();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const orderCtx = useOrderContext();

  const formatters = useIntl();

  const onRemove = () => {
    // TODO: fix this when we have multiple applied offers
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.resetAppliedOffers());
  };

  const { isValid = true, textOfferRequirementsNotMet = '' } = useMemo(() => {
    const { formatMessage } = formatters;
    const available = checkLoyaltyOfferAvailability(selectedLoyaltyOffer);
    const ruleErrors = offersFeedbackMap[selectedLoyaltyOffer.loyaltyEngineId || ''];
    const hasRuleErrors = Boolean(ruleErrors?.length);
    const { isCartTotalNegative = false } = orderCtx?.calculateCartTotalWithDiscount?.() || {};

    let errorMessage: React.ReactNode | undefined;
    if (!available) {
      errorMessage = formatMessage({ id: 'offerNotAvailableInRestaurantPos' });
    } else if (isCartTotalNegative) {
      errorMessage = formatMessage({ id: 'cartOfferRequirementsNotMetError' });
    } else if (hasRuleErrors) {
      // Show first error only
      errorMessage = getEvaluationResultMessage({
        evaluationResult: ruleErrors[0],
        formatters,
        incentiveType: PromotionType.OFFER,
      });
    }

    return {
      isValid: available && !hasRuleErrors && !isCartTotalNegative,
      textOfferRequirementsNotMet: errorMessage,
    };
  }, [
    checkLoyaltyOfferAvailability,
    formatters,
    offersFeedbackMap,
    orderCtx,
    selectedLoyaltyOffer,
  ]);

  return (
    <CartOffer
      selectedOffer={selectedLoyaltyOffer}
      onRemove={onRemove}
      textOfferRequirementsNotMet={textOfferRequirementsNotMet}
      isValid={isValid}
    />
  );
};
